import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/Base/Button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/Base/Form";
import { Input } from "@/components/Base/Input";
import { CreateEmployeeDto, Employee, EmployeeJobTitle } from "@/types/v2";
import { getServiceAdvisorColor } from "@/lib/employees";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Base/Select";
import { useTerminologyReplacer } from "@/hooks/useTerminologyReplacer";
import { DEFAULT_EMPLOYEE_COLOR, JOB_TITLES } from "@/lib/const";

export type EmployeeFormProps = {
  onSubmit?: (values: CreateEmployeeDto) => Promise<void> | void;
  onCancel?: () => void;
  initialData?: Employee;
  submitTitle?: string;
  cancelTitle?: string;
};

const createFormSchema = (isShopHeroEmployee: boolean) => {
  const baseStringSchema = z.string();

  return z.object({
    firstName: isShopHeroEmployee
      ? baseStringSchema.min(1, "First name is required")
      : baseStringSchema.optional(),
    lastName: isShopHeroEmployee
      ? baseStringSchema.min(1, "Last name is required")
      : baseStringSchema.optional(),
    email: isShopHeroEmployee
      ? baseStringSchema
          .email("Enter a valid email")
          .min(1, "Email is required")
      : baseStringSchema.optional(),
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Invalid color format"),
    schemaVersion: z.number(),
    sourceLastUpdated: z.date(),
    shopHeroJobTitle: z.string().optional(),
  });
};

export const EmployeeForm = ({
  onSubmit,
  onCancel,
  initialData,
  cancelTitle,
  submitTitle,
}: EmployeeFormProps) => {
  const { terminolizeAuto } = useTerminologyReplacer();
  // Determine if this is a Shop Hero created employee
  const isShopHeroEmployee =
    initialData?._creationSource === "client" || !initialData?._creationSource;

  const formSchema = createFormSchema(isShopHeroEmployee);

  const defaultValues = {
    firstName: initialData?.firstName ?? "",
    lastName: initialData?.lastName ?? "",
    email: initialData?.email ?? "",
    color:
      initialData?.color ??
      (initialData
        ? getServiceAdvisorColor(initialData.id, [initialData])
        : DEFAULT_EMPLOYEE_COLOR),
    schemaVersion: 2,
    sourceLastUpdated: new Date(),
    shopHeroJobTitle: initialData?.shopHeroJobTitle ?? "other",
  };

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const handleFormSubmit = async (values: z.infer<typeof formSchema>) => {
    if (onSubmit) {
      // If not a Shop Hero employee, only submit the color
      if (!isShopHeroEmployee) {
        return onSubmit({
          color: values.color,
          schemaVersion: 2,
          sourceLastUpdated: initialData?.sourceLastUpdated ?? new Date(),
          firstName: initialData?.firstName ?? "",
          lastName: initialData?.lastName ?? "",
          email: initialData?.email ?? "",
          shopHeroJobTitle:
            (values.shopHeroJobTitle as EmployeeJobTitle) ?? "other",
        });
      }
      return onSubmit(values as CreateEmployeeDto);
    }
  };

  return (
    <div className="flex w-full flex-col gap-2">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleFormSubmit)}
          className="space-y-4"
        >
          <div className="flex flex-col gap-2">
            {isShopHeroEmployee && (
              <>
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          placeholder="First Name"
                          disabled={form.formState.isSubmitting}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          placeholder="Last Name"
                          disabled={form.formState.isSubmitting}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="Email"
                          disabled={form.formState.isSubmitting}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}
            <FormField
              control={form.control}
              name="color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Color</FormLabel>
                  <FormControl>
                    <Input
                      type="color"
                      placeholder="#000000"
                      className="w-32 rounded-lg bg-white p-1"
                      disabled={form.formState.isSubmitting}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={"shopHeroJobTitle"}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Job Title</FormLabel>
                  <FormControl>
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value); // Update form field value
                      }}
                      value={field.value} // Ensure the correct value is displayed
                      disabled={form.formState.isSubmitting}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder={"Job Title"} />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.keys(JOB_TITLES).map((key) => (
                          <SelectItem value={key} key={key}>
                            {terminolizeAuto(
                              JOB_TITLES[key as keyof typeof JOB_TITLES],
                              ["singular"]
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="mt-5 flex flex-row justify-end">
            <Button
              type="button"
              className="mr-5 mt-5"
              variant="secondary"
              onClick={handleCancel}
            >
              {cancelTitle ?? "Cancel"}
            </Button>
            <Button
              type="submit"
              className="mt-5"
              variant="accent"
              disabled={!form.formState.isValid || form.formState.isSubmitting}
            >
              {submitTitle ?? "Create"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};
